import { useState, useEffect } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export default function Template() {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('header'); // 'header' nebo 'footer'
  const [previewMode, setPreviewMode] = useState('html'); // 'html' nebo 'markdown'

  useEffect(() => {
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/template`);
      const data = await response.json();
      setTemplate(data.template);
    } catch (error) {
      console.error('Error fetching template:', error);
      setMessage({ type: 'error', text: 'Chyba při načítání šablony' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_URL}/api/template`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(template),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Šablona byla úspěšně uložena!' });
        setTemplate(data.template);
      } else {
        setMessage({ type: 'error', text: data.error || 'Chyba při ukládání' });
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setMessage({ type: 'error', text: 'Chyba při ukládání šablony' });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, format, value) => {
    setTemplate({
      ...template,
      [section]: {
        ...template[section],
        [format]: value
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Načítám šablonu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Šablona Newsletteru</h1>
          <p className="text-gray-600">Upravte hlavičku a patičku newsletteru</p>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('header')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'header'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Hlavička (Header)
            </button>
            <button
              onClick={() => setActiveTab('footer')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'footer'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Patička (Footer)
            </button>
          </nav>
        </div>

        {/* Editor Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* HTML Editor */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 text-gray-900">HTML Verze</h2>
            <textarea
              value={template?.[activeTab]?.html || ''}
              onChange={(e) => updateField(activeTab, 'html', e.target.value)}
              className="w-full h-64 font-mono text-sm p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Zadejte HTML kód..."
            />
          </div>

          {/* Markdown Editor */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 text-gray-900">Markdown Verze</h2>
            <textarea
              value={template?.[activeTab]?.markdown || ''}
              onChange={(e) => updateField(activeTab, 'markdown', e.target.value)}
              className="w-full h-64 font-mono text-sm p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Zadejte Markdown..."
            />
          </div>
        </div>

        {/* Preview Section */}
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Náhled</h2>
            <div className="flex gap-2">
              <button
                onClick={() => setPreviewMode('html')}
                className={`px-4 py-2 text-sm font-medium rounded ${
                  previewMode === 'html'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                HTML
              </button>
              <button
                onClick={() => setPreviewMode('markdown')}
                className={`px-4 py-2 text-sm font-medium rounded ${
                  previewMode === 'markdown'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Markdown
              </button>
            </div>
          </div>

          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 min-h-[200px]">
            {previewMode === 'html' ? (
              <div
                dangerouslySetInnerHTML={{ __html: template?.[activeTab]?.html || '' }}
              />
            ) : (
              <pre className="whitespace-pre-wrap font-mono text-sm text-gray-700">
                {template?.[activeTab]?.markdown || ''}
              </pre>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              saving ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {saving ? 'Ukládám...' : 'Uložit šablonu'}
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">💡 Tipy pro editaci:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• HTML verze se použije v emailových klientech</li>
            <li>• Markdown verze se použije pro preview a archivaci</li>
            <li>• Používejte inline styly v HTML pro lepší kompatibilitu s emailovými klienty</li>
            <li>• Header se vloží na začátek každého newsletteru</li>
            <li>• Footer se vloží na konec každého newsletteru</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
